import type { Camera } from '@react-three/fiber';
import { useThree } from '@react-three/fiber';
import { partial } from 'lodash-es';
import { useEffect } from 'react';
import type { Quaternion, Vector3 } from 'three';
import { OrthographicCamera } from 'three';

import { TOOL_CONST } from '@/constant';
import { useLabelingBoxContext } from '@/provider';
import { useToolSurfaceCameraStore } from '@/store';
import { getDiagonalLength, getVectorByAxis } from '@/util';

import { useToolPcdCanvasContext } from './ToolPcdCanvas';

// TODO: Camera.far와 동기화 필요
// NOTE: 이 값이 box의 width, heihgt, depth중 하나보다 작을 경우 삼분할 화면 컨트롤 미작동
const OFFSET_SCALAR_FACTOR = 500;

const setCameraToView = (
  camera: Camera,
  position: Vector3,
  quaternion: Quaternion,
  setZoom: (zoom: number) => void,
  diagonalLength: number,
  offset: Vector3,
) => {
  if (!(camera instanceof OrthographicCamera)) {
    console.warn('`camera` is not OrthographicCamera');
    return;
  }
  const ZOOM_FACTOR = 0.7;

  const currentWidth = camera.right - camera.left;
  const currentHeight = camera.top - camera.bottom;
  const zoom =
    (ZOOM_FACTOR * Math.min(currentHeight, currentWidth)) / diagonalLength;

  offset.applyQuaternion(quaternion);

  camera.position.copy(position.clone().add(offset));
  camera.lookAt(position);

  setZoom(zoom);
};

const BoxFocusController = () => {
  const { camera, viewport } = useThree();
  const { selectedLabelingBoxList: selectedBoxList } = useLabelingBoxContext();
  const { viewMode } = useToolPcdCanvasContext();
  const firstSelectedBox = selectedBoxList?.[0];

  useEffect(() => {
    if (!firstSelectedBox) return;

    const boxPosition = firstSelectedBox.boxMesh.position;
    const boxQuaternion = firstSelectedBox.boxMesh.quaternion;

    const setCamera = partial(
      setCameraToView,
      camera,
      boxPosition,
      boxQuaternion,
      partial(useToolSurfaceCameraStore.getState().setZoom, viewMode),
    );

    switch (viewMode) {
      case TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP:
        camera.lookAt(0, 0, 0);
        camera.up.set(0, 1, 0);
        setCamera(
          getDiagonalLength(
            firstSelectedBox.dimension[0],
            firstSelectedBox.dimension[1],
          ),
          getVectorByAxis('z').multiplyScalar(OFFSET_SCALAR_FACTOR),
        );
        break;
      case TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE: {
        camera.lookAt(0, 0, 0);
        camera.up.set(0, 0, 1);
        setCamera(
          getDiagonalLength(
            firstSelectedBox.dimension[0],
            firstSelectedBox.dimension[2],
          ),
          getVectorByAxis('y').negate().multiplyScalar(OFFSET_SCALAR_FACTOR),
        );
        break;
      }
      case TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT: {
        camera.lookAt(0, 0, 0);
        camera.up.set(0, 0, 1);
        setCamera(
          getDiagonalLength(
            firstSelectedBox.dimension[1],
            firstSelectedBox.dimension[2],
          ),
          getVectorByAxis('x').multiplyScalar(OFFSET_SCALAR_FACTOR),
        );
        break;
      }
    }

    camera.updateProjectionMatrix();
  }, [firstSelectedBox?.id, camera, viewport.aspect]);

  return <></>;
};

export default BoxFocusController;
